import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Save, ChevronDown, ChevronUp, ChevronRight, ArrowRight, RefreshCw, Loader2, Zap, CheckCircle2 } from "lucide-react";
import { useAuth, apiCall } from "../App";
import { toast } from "sonner";

function TatkalikQuiz({ module = "relationship", isCoachView = false, viewAsUserId = null }) {
  const { user, token } = useAuth();
  const [triggers, setTriggers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [results, setResults] = useState(null);
  const [hasResponse, setHasResponse] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const fetchData = useCallback(async () => {
    const uidParam = isCoachView && viewAsUserId ? `&user_id=${viewAsUserId}` : "";
    const [triggerRes, resultRes] = await Promise.all([
      apiCall("GET", "/tatkalik/triggers", null, token),
      apiCall("GET", `/tatkalik/results?module=${module}${uidParam}`, null, token),
    ]);
    if (triggerRes.success) setTriggers(triggerRes.data.triggers || []);
    if (resultRes.success) {
      setHasResponse(resultRes.data.has_response);
      setResults(resultRes.data.selected || []);
      if (resultRes.data.has_response) {
        setSelectedIds(resultRes.data.selected.map(t => t.id));
      }
    }
    setLoading(false);
  }, [token, module, isCoachView, viewAsUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleTrigger = (id) => {
    if (isCoachView) return;
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await apiCall("POST", "/tatkalik/save", {
      module,
      selected_trigger_ids: selectedIds,
    }, token);
    if (res.success) {
      setResults(res.data.results || []);
      setHasResponse(true);
      toast.success(`Saved ${res.data.selected_count} contemporary habit triggers`);
    } else {
      console.error("Tatkalik save failed:", res.error || res);
      toast.error(res.error?.detail || res.error || "Save failed. Please retry or contact support.");
    }
    setSaving(false);
  };

  const handleRetake = async () => {
    // Call backend to actually delete the data from database
    await apiCall("POST", "/tatkalik/retake", { module }, token);
    setSelectedIds([]);
    setHasResponse(false);
    setResults([]);
    setShowTest(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-divine-gold/50" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-testid="tatkalik-quiz">
      {/* LEFT: Test Card */}
      <div className={`p-4 rounded-xl border transition-all ${
        hasResponse ? "bg-emerald-500/5 border-emerald-500/20" : "bg-cosmic-light/5 border-divine-gold/10"
      }`} data-testid="tatkalik-test-card">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${hasResponse ? "bg-emerald-500/10" : "bg-divine-gold/10"}`}>
            <Zap className={`w-5 h-5 ${hasResponse ? "text-emerald-400" : "text-yellow-400"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-heading truncate ${hasResponse ? "text-emerald-400" : "text-yellow-400"}`}>TATKALIK</p>
              {hasResponse && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
            </div>
            <p className="text-xs text-chakra-crown/40 font-heading">Contemporary Habits</p>
            <p className="text-xs text-chakra-crown/50 mt-1.5 leading-relaxed line-clamp-2">
              Identify your contemporary limiting habits affecting your life and relationship.
            </p>
            <span className="text-[10px] text-chakra-crown/30 mt-1 block">{triggers.length} triggers</span>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <button onClick={() => setShowTest(!showTest)}
              className="p-1.5 rounded-lg hover:bg-divine-gold/10 transition-colors" data-testid="tatkalik-test-toggle">
              {showTest
                ? <ChevronDown className={`w-4 h-4 ${hasResponse ? "text-emerald-400/40" : "text-divine-gold/40"}`} />
                : <ChevronRight className={`w-4 h-4 ${hasResponse ? "text-emerald-400/40" : "text-divine-gold/40"}`} />}
            </button>
            {hasResponse && !isCoachView && (
              <button onClick={handleRetake} disabled={saving}
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors"
                data-testid="tatkalik-retake-btn">
                <RefreshCw className="w-2.5 h-2.5" /> Retake
              </button>
            )}
          </div>
        </div>

        {/* Expandable trigger selection */}
        <AnimatePresence>
          {showTest && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="mt-3 pt-3 border-t border-divine-gold/10 space-y-3">
                <p className="text-chakra-crown/40 text-[10px]">
                  Click to select all that apply ({selectedIds.length} of {triggers.length} selected)
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-[340px] overflow-y-auto pr-1">
                  {triggers.map((t) => {
                    const isSelected = selectedIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => toggleTrigger(t.id)}
                        disabled={isCoachView}
                        data-testid={`tatkalik-trigger-${t.id}`}
                        className={`
                          inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-all duration-200
                          border cursor-pointer
                          ${isSelected
                            ? "bg-divine-gold/15 border-divine-gold/40 text-divine-gold"
                            : "bg-cosmic-void/40 border-divine-gold/10 text-chakra-crown/60 hover:border-divine-gold/25 hover:text-chakra-crown/80"
                          }
                          ${isCoachView ? "cursor-default" : ""}
                        `}
                      >
                        {isSelected && <Check className="w-3 h-3 text-divine-gold" />}
                        {t.trigger}
                      </button>
                    );
                  })}
                </div>
                {!isCoachView && (
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSave}
                      disabled={selectedIds.length === 0 || saving}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold border border-divine-gold/30 text-xs disabled:opacity-50 transition-all"
                      data-testid="tatkalik-save-btn"
                    >
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Save
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* RIGHT: Perception Shift Map Result Card */}
      <div className="rounded-xl border border-divine-gold/10 bg-cosmic-deep/30 overflow-hidden" data-testid="tatkalik-results-card">
        <button
          onClick={() => setShowResults(!showResults)}
          className="w-full flex items-center justify-between px-4 py-3"
          data-testid="tatkalik-results-toggle"
        >
          <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /><span className="text-divine-gold text-xs font-heading tracking-wider">PERCEPTION SHIFT MAP</span></span>
          <div className="flex items-center gap-2">
            {hasResponse && results?.length > 0 && (
              <span className="text-chakra-crown/30 text-[10px]">{results.length} pairs</span>
            )}
            {showResults
              ? <ChevronUp className="w-3.5 h-3.5 text-divine-gold/40" />
              : <ChevronDown className="w-3.5 h-3.5 text-divine-gold/40" />}
          </div>
        </button>
        <AnimatePresence>
          {showResults && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="px-4 pb-4">
                {!hasResponse || !results || results.length === 0 ? (
                  <p className="text-chakra-crown/30 text-sm text-center py-4" data-testid="tatkalik-no-results">
                    Select your contemporary habit triggers and save to reveal the perception shift map
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1" data-testid="tatkalik-results-list">
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center px-2 pb-1 border-b border-divine-gold/10">
                      <span className="text-[9px] tracking-wider text-red-400/60 font-semibold uppercase">False Perception</span>
                      <span className="w-4" />
                      <span className="text-[9px] tracking-wider text-emerald-400/60 font-semibold uppercase">Real Perception</span>
                    </div>
                    {results.map((t, idx) => (
                      <motion.div
                        key={t.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.04 }}
                        className="group rounded-lg bg-cosmic-void/30 border border-divine-gold/5 hover:border-divine-gold/15 transition-colors"
                        data-testid={`tatkalik-pair-${t.id}`}
                      >
                        <div className="px-2 py-1 border-b border-divine-gold/5">
                          <span className="text-divine-gold/80 text-[10px] font-medium">{t.trigger}</span>
                        </div>
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center px-2 py-1.5">
                          <span className="text-red-400/70 text-xs">{t.false_perception}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-divine-gold/40 shrink-0" />
                          <span className="text-emerald-400/90 text-xs font-medium">{t.real_perception}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default TatkalikQuiz;
